import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listPeople, type Person } from './api';
import { NativeSelect } from './NativeSelect';

type People =
  | { status: 'loading' }
  | { status: 'ready'; people: readonly Person[] }
  | {
      status: 'unavailable';
    };

/**
 * «Per conto di» (spec §3.1.1, the board's step 3): only for holders of expenses:expense:manage.
 * A new expense chooses its owner among the organization's people, «Me» by default (`''`); an
 * existing one shows whose it is, read-only, because the owner does not change.
 */
export function OwnerField({
  value,
  onChange,
  existingOwner,
}: {
  /** The chosen owner's user id, `''` for the caller. */
  readonly value: string;
  readonly onChange: (userId: string) => void;
  /** Edit mode: the owner's name. */
  readonly existingOwner?: string | undefined;
}) {
  const { t } = useTranslation('expenses');
  const uid = useId();
  const [people, setPeople] = useState<People>({ status: 'loading' });
  const editing = existingOwner !== undefined;

  useEffect(() => {
    if (editing) return;
    let active = true;
    void listPeople().then((outcome) => {
      if (!active) return;
      setPeople(
        outcome.ok ? { status: 'ready', people: outcome.value } : { status: 'unavailable' },
      );
    });
    return () => {
      active = false;
    };
  }, [editing]);

  // An owner kept from an earlier visit who is no longer among the organization's people.
  const unknownOwner =
    people.status === 'ready' && value !== '' && !people.people.some((p) => p.userId === value);
  useEffect(() => {
    if (unknownOwner) onChange('');
  }, [unknownOwner, onChange]);

  if (editing) {
    return (
      <p className="rounded-lg bg-muted p-3 text-sm" data-field="owner">
        {t('capture.owner.existing', { name: existingOwner })}
      </p>
    );
  }

  const chosen =
    people.status === 'ready' ? people.people.find((p) => p.userId === value) : undefined;

  return (
    <div className="space-y-1" data-field="owner">
      <label htmlFor={`${uid}-owner`} className="text-sm font-medium">
        {t('capture.owner.label')}
      </label>
      <NativeSelect
        id={`${uid}-owner`}
        value={value}
        disabled={people.status !== 'ready'}
        aria-describedby={`${uid}-owner-hint`}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{t('capture.owner.me')}</option>
        {people.status === 'ready' &&
          people.people.map((person) => (
            <option key={person.userId} value={person.userId}>
              {person.displayName}
            </option>
          ))}
      </NativeSelect>
      {people.status === 'unavailable' ? (
        <p id={`${uid}-owner-hint`} role="alert" className="text-sm text-destructive">
          {t('capture.save.problems.PEOPLE_UNAVAILABLE')}
        </p>
      ) : (
        <p id={`${uid}-owner-hint`} className="text-xs text-muted-foreground">
          {chosen
            ? t('capture.owner.hintOther', { name: chosen.displayName })
            : t('capture.owner.hint')}
        </p>
      )}
    </div>
  );
}
